import { clsx } from 'clsx';
import { Badge, Button } from '@/shared/ui';
import patterns from '@/shared/ui/styles/patterns.module.css';
import type { PreviewResult, PreviewRow } from '../api/verificationApi';
import { formatDateTime, pluralize } from '../utils/format';
import styles from './PreviewStep.module.css';

interface PreviewStepProps {
  preview: PreviewResult;
  onBack: () => void;
  onImport: () => void;
  importing: boolean;
}

function ReadyBadge({ row }: { row: PreviewRow }) {
  const suffix = row.duplicate ? ' · updates existing' : '';
  if (row.readiness === 'ready') {
    return <Badge variant="success">Ready{suffix}</Badge>;
  }
  if (row.readiness === 'needs_payer') {
    return <Badge variant="warning">Needs payer{suffix}</Badge>;
  }
  const missing = row.missingFields.length > 0 ? row.missingFields.join(', ') : 'fix';
  return (
    <Badge variant="warning">
      Needs {missing}
      {suffix}
    </Badge>
  );
}

export function PreviewStep({ preview, onBack, onImport, importing }: PreviewStepProps) {
  const { rows, counts } = preview;
  const needFix = counts.needsFix + counts.needsPayer;

  return (
    <div className={styles.root}>
      <p className={patterns.pageSubtitle}>
        Review before importing. Rows missing required detail can still import — they'll just be
        flagged for a fix before the check can run.
      </p>

      <div className={patterns.gridTable}>
        <div className={clsx(patterns.gridTableHeader, styles.cols)}>
          <span>Patient</span>
          <span>DOB</span>
          <span>Appointment</span>
          <span>Carrier</span>
          <span>Member ID</span>
          <span>Ready?</span>
        </div>
        {rows.length === 0 && (
          <div className={patterns.gridTableEmpty}>Nothing to import — the file had no rows.</div>
        )}
        {rows.map((r) => (
          <div key={r.rowIndex} className={clsx(patterns.gridTableRow, styles.cols)}>
            <div className={patterns.cellPrimary}>{r.preview.patientName || '—'}</div>
            <div className={r.preview.dobMasked ? patterns.cellSecondary : styles.missing}>
              {r.preview.dobMasked ?? 'missing'}
            </div>
            <div>
              <div className={patterns.cellSecondary}>{r.preview.appointmentType || '—'}</div>
              {r.preview.appointmentAt && (
                <div className={patterns.cellSmMuted}>
                  {formatDateTime(r.preview.appointmentAt)}
                </div>
              )}
            </div>
            <div className={r.preview.carrier ? patterns.cellSecondary : styles.missing}>
              {r.preview.carrier || 'missing'}
            </div>
            <div className={clsx(patterns.cellSecondary, styles.mono)}>
              {r.preview.memberIdMasked || '—'}
            </div>
            <div>
              <ReadyBadge row={r} />
              {r.message && <div className={patterns.cellSmMuted}>{r.message}</div>}
            </div>
          </div>
        ))}
      </div>

      <div className={styles.footer}>
        <div className={styles.summary}>
          <strong>{counts.ready} ready</strong> · {pluralize(needFix, 'needs', 'need')} a fix ·{' '}
          {counts.duplicates} already exist (will be updated)
        </div>
        <div className={styles.actions}>
          <Button variant="ghost" onClick={onBack} disabled={importing}>
            Back
          </Button>
          <Button onClick={onImport} isLoading={importing} disabled={counts.total === 0}>
            Import {pluralize(counts.total, 'appointment')}
          </Button>
        </div>
      </div>
    </div>
  );
}
